import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/UserManagementSummaryPageFilterSection';
import { Form, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import {
	DISPLAY_DATE_TIME_FORMAT,
	DISPLAY_DATETIME_12H_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { FormType } from '@/api/enum/formType.enum';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import {
	StaffSimpleDTO,
	StaffSimpleFilter,
} from '@/api/DTO/user-management/userManagementList.interface';
import { useGetUserListQuery } from '@/api/user-management/userManagementListApiSlice';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useGetUser } from '@/hooks/useGetUser';
import { PermissionEnum } from '@/api/enum/common.enum';

export default function UserManagementSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const { userInfo } = useGetUser();

	const defaultPropertyFilter: StaffSimpleFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		roles: [],
		existInAD: [1],
	};
	const defaultValues: DefaultValues<StaffSimpleFilter> = defaultPropertyFilter;
	const methods = useForm<StaffSimpleFilter>({
		defaultValues,
	});
	const transformedSearchParams: StaffSimpleFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'roles',
			'existInAD',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetUserListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: StaffSimpleDTO[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				lastLogin: !item.lastLogin
					? '-'
					: dayjs(item.lastLogin).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as StaffSimpleFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: t('user.name'),
			flex: 20,
		},
		{
			field: 'username',
			headerName: t('user.userId'),
			flex: 20,
		},
		{
			field: 'branchName',
			headerName: t('user.unit'),
			flex: 20,
		},
		{
			field: 'lastLogin',
			headerName: t('user.lastOnlineDateTime'),
			flex: 20,
		},
		{
			field: 'roleName',
			headerName: t('user.role'),
			flex: 20,
		},
		{
			field: 'disabled',
			headerName: t('member.status'),
			flex: 20,
			renderCell: (params) => (
				<Chip
					color={params.row.disabled ? 'chipPrimary' : 'chipTertiary'}
					label={
						<Box
							sx={{
								width: '100%',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
							}}
						>
							{params.row.disabled ? t('user.suspended') : t('user.active')}
						</Box>
					}
				/>
			),
		},
		{
			field: 'existInAD',
			headerName: t('user.existInAD'),
			flex: 20,
			renderCell: (params) => (params.row.existInAD ? t('Yes') : t('No')),
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				userInfo?.permissionList?.includes(
					PermissionEnum.ViewSystemUserAccount
				) && (
					<Link
						to={`/user-management/user-management-list/detail/${params.id}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.userManagementList')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					{userInfo?.permissionList?.includes(
						PermissionEnum.CreateSystemUserAccount
					) && (
						<Button
							variant='contained'
							endIcon={<AddCircleOutlineOutlinedIcon />}
							onClick={() => {
								navigate(`/user-management/user-management-list/create`);
							}}
							style={{ alignItems: 'center' }}
						>
							{t('button.create')}
						</Button>
					)}
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
